import type { SubscriptionStatus, Product, ProductOffering, SubscriptionStatusResponse, SubscriptionPlatformInfo } from '../types/subscription';
import coreApi from '../api/coreApi';

// Initial product definitions with loading state
export let products: Record<string, Product> = {
    weekly: {
        id: '',
        title: 'Weekly Plan',
        description: 'Perfect for trying out our features',
        period: 'week',
        price: 'Loading...',
        rawPrice: 0,
        currency: 'USD'
    },
    yearly: {
        id: '',
        title: 'Yearly Plan',
        description: 'Best value for committed users',
        period: 'year',
        price: 'Loading...',
        rawPrice: 0,
        currency: 'USD'
    }
};

export async function getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    try {
        console.log('Fetching subscription status from backend...');
        const response = await coreApi.get<SubscriptionStatusResponse>('/subscriptions/status');
        console.log('Subscription status:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error fetching subscription status:', error);
        throw error;
    }
}

export async function fetchProducts(): Promise<void> {
    try {
        console.log('Fetching products from backend...');
        const response = await coreApi.get<ProductOffering>('/subscriptions/products?platform=web');
        
        if (!response.data) {
            throw new Error('No data received from products endpoint');
        }

        const data = response.data;
        console.log('Received products:', data);

        // Update local products with prices from backend
        if (data.offerings?.current?.availablePackages) {
            data.offerings.current.availablePackages.forEach((pkg) => {
                if (pkg.packageType === '$rc_weekly') {
                    products.weekly = {
                        ...products.weekly,
                        id: pkg.identifier,
                        price: pkg.product.price,
                        rawPrice: parseFloat(pkg.product.price.replace(/[^0-9.]/g, '')),
                        currency: pkg.product.currency
                    };
                } else if (pkg.packageType === '$rc_annual') {
                    products.yearly = {
                        ...products.yearly,
                        id: pkg.identifier,
                        price: pkg.product.price,
                        rawPrice: parseFloat(pkg.product.price.replace(/[^0-9.]/g, '')),
                        currency: pkg.product.currency
                    };
                }
            });
        }

        console.log('Updated products:', products);
    } catch (error) {
        console.error('Error fetching products:', error);
        throw error;
    }
}

export async function purchasePackage(productId: string): Promise<void> {
    try {
        console.log('Creating checkout session for:', { productId });

        const response = await coreApi.post('/subscriptions/checkout', {
            productId
        });

        if (!response.data?.url) {
            throw new Error('No checkout URL received');
        }

        // Redirect to Stripe checkout
        window.location.href = response.data.url;
    } catch (error) {
        console.error('Error purchasing package:', error);
        throw error;
    }
}

export async function manageSubscription(): Promise<string> {
    try {
        const response = await coreApi.get<{ url: string }>('/subscriptions/manage');
        if (!response.data?.url) {
            throw new Error('No management URL available');
        }
        return response.data.url;
    } catch (error) {
        console.error('Error getting management URL:', error);
        throw error;
    }
}

// Helper functions for subscription management
export function getManagementUrl(subscription: SubscriptionPlatformInfo): string | undefined {
    return subscription.managementUrl;
}

export function isPlatformSubscription(subscription: SubscriptionPlatformInfo, platform: 'ios' | 'android' | 'web'): boolean {
    return subscription.platform === platform;
}

export function getManagementButtonText(subscription: SubscriptionPlatformInfo): string {
    switch (subscription.platform) {
        case 'ios':
            return 'Manage in App Store';
        case 'android':
            return 'Manage in Play Store';
        case 'web':
            return 'Manage Subscription';
        default:
            return 'Manage Subscription';
    }
}
  